import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, LockKeyhole } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Card, CardContent } from "@/components/ui/card";
import { CorrectionsPanel } from "@/components/CorrectionsPanel";
import { SUBMISSION_STATUS } from "@/lib/strings";

const periodLabel = (value) => (value === "IN_PROGRESS" ? "In corso" : value || "—");
const questionnaireLabel = (value) => SUBMISSION_STATUS[value]?.label || value || "—";

export default function Incongruenze() {
  const { companyId, period } = useTenant();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!companyId || !period?.id || (period.company_id && period.company_id !== companyId)) return;
    try {
      const { data: response } = await api.get(
        `/companies/${companyId}/periods/${period.id}/data-quality/issues`,
      );
      setData(response);
      setError(null);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return <p className="text-sm text-red-700" data-testid="incongruenze-error">{error}</p>;
  }
  if (!data || !data.status?.validation) {
    return <p className="text-sm text-slate-600" data-testid="incongruenze-loading">Caricamento…</p>;
  }
  // Le risposte incoerenti restano correggibili anche dopo l'invio (correzione guidata).
  const canFix = !data.read_only || ["INTEGRATION_REQUIRED", "READY_FOR_RESUBMISSION"].includes(data.effective_status);

  return (
    <div className="max-w-4xl space-y-6" data-testid="incongruenze-page">
      <header>
        <h1 className="flex items-center gap-3 text-2xl font-bold text-slate-900 md:text-3xl">
          <AlertTriangle className="h-7 w-7 text-red-700" aria-hidden="true" />
          Incongruenze da correggere
        </h1>
        <dl className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2">
          <div data-testid="integrity-period-status">Periodo: <strong>{periodLabel(data.status.period)}</strong></div>
          <div data-testid="integrity-questionnaire-status">
            Questionario: <strong>{questionnaireLabel(data.status.questionnaire)}</strong>
          </div>
          <div data-testid="integrity-validation-status">
            Validazione: <strong><span data-testid="integrity-validation-count">{data.status.validation.inconsistent}</span> incongruenze bloccanti</strong>
          </div>
          <div data-testid="integrity-approval-status">
            Approvazione: <strong>{data.status.approval === "APPROVATO" ? "approvato" : "non approvato"}</strong>
          </div>
        </dl>
      </header>

      {data.read_only && !canFix && (
        <div className="flex gap-3 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950"
          data-testid="incongruenze-readonly-notice">
          <LockKeyhole className="h-5 w-5 shrink-0" aria-hidden="true" />
          Il questionario è in sola lettura: per correggere questi dati è necessaria una riapertura autorizzata.
        </div>
      )}

      {data.issues.length === 0 ? (
        <Card data-testid="incongruenze-empty">
          <CardContent className="p-6 text-sm text-slate-600">Nessuna incongruenza bloccante aperta.</CardContent>
        </Card>
      ) : (
        <CorrectionsPanel corrections={data.issues} readOnly={!canFix} testid="incongruenze-panel" />
      )}
    </div>
  );
}
